import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  appointmentFormSchema,
  defaultFormValues,
  type AppointmentFormValues,
} from '@/types/appointment-form';

/**
 * QueryClient is mounted ONCE at the app root (`src/main.tsx` reading
 * from `src/app/providers/query-client.ts`). Do not introduce another
 * `<QueryClientProvider>` here — multiple clients would split the cache
 * and the global error handler would stop seeing these queries.
 */

interface AppointmentFormProviderProps {
  initialValues?: Partial<AppointmentFormValues>;
  children: React.ReactNode;
}

function todayStartIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function AppointmentFormProvider({
  initialValues,
  children,
}: AppointmentFormProviderProps) {
  // Seed selectedDate to today's start-of-day so dependent queries
  // (provider slots, available slots) get a stable, non-null date in their
  // queryKey from the first render. Without this, the fallback
  // `selectedDate ?? new Date().toISOString()` produces a fresh ISO every
  // millisecond and refires the query on every render.
  const form = useForm({
    resolver: zodResolver(appointmentFormSchema),
    defaultValues: {
      ...defaultFormValues,
      selectedDate: todayStartIso(),
      ...initialValues,
    },
    mode: 'onChange',
  });

  // RHF quirk: `formState.isValid` starts `false` even when defaults pass
  // schema, and only flips after a real validation run. Trigger once on
  // mount so the Save button reflects the actual state. Without this,
  // Save stays disabled with an empty `errors` object — confusing both
  // the user and the diagnostic tooltip.
  useEffect(() => {
    form.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <FormProvider {...form}>{children}</FormProvider>;
}
